import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

import { inventoryService } from '../../services/inventoryService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import PaynkolayIframeForm from './components/PaynkolayIframeForm';


import Input from '../../components/ui/Input';
import { ShoppingCart, CreditCard, Truck, AlertCircle, CheckCircle, Lock, Shield, Building2 } from 'lucide-react';
import { TURKEY_CITIES, TURKEY_DISTRICTS } from '../../constants/turkeyData';
import couponService from '../../services/couponService';
import { toTurkishTitleCase } from '../../utils/turkishValidation';

// ─────────────────────────────────────────────────────────────────────────────
// SUCCESS SCREEN — completely isolated, no cart/auth hooks
// ─────────────────────────────────────────────────────────────────────────────
function PaymentSuccessScreen() {
  useEffect(() => {
    // If loaded inside an iframe (bank callback redirect), break out to top window
    if (window.top !== window.self) {
      window.top.location.href = window.location?.href;
      return;
    }
    try {
      localStorage.removeItem('cartItems');
      localStorage.removeItem('order_id');
      localStorage.removeItem('draft_order_id');
      localStorage.removeItem('checkout_order_id');
      localStorage.removeItem('pending_order');
    } catch (_) {}
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Ödeme Başarılı!</h1>
          <p className="text-gray-600 mb-2">Ödemeniz başarıyla alınmıştır.</p>
          <p className="text-gray-500 text-sm mb-8">Siparişiniz en kısa sürede hazırlanacak ve tarafınıza iletilecektir.</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/order-history'}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Siparişlerimi Görüntüle
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FAIL SCREEN — completely isolated, no cart/auth hooks
// ─────────────────────────────────────────────────────────────────────────────
function PaymentFailScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <div className="flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Ödeme Başarısız</h1>
          <p className="text-gray-600 mb-8">Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin veya farklı bir ödeme yöntemi seçin.</p>
          <div className="space-y-3">
            <button
              onClick={() => { window.history?.replaceState(null, '', '/checkout'); window.location?.reload(); }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Tekrar Dene
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSOLUTE WRAPPER — reads payment_status via useEffect BEFORE any other
// component tree (CheckoutForm, CartContext, AuthContext) is ever instantiated.
// ─────────────────────────────────────────────────────────────────────────────
export default function CheckoutPageWrapper() {
  // null = not yet determined, '' = no status param, 'success'/'fail'/'error' = payment return
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const status = params?.get('payment_status');
      setPaymentStatus(status || '');
    } catch (_) {
      setPaymentStatus('');
    }
  }, []);

  // Listen for postMessage from Paynkolay iframe callback
  useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data && event?.data?.type === "PAYNKOLAY_RESULT") {
        if (event?.data?.status === "success") {
          window.location.href = "/checkout?payment_status=success";
        } else {
          window.location.href = "/checkout?payment_status=error";
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Still determining — show nothing (avoids flash of checkout form)
  if (paymentStatus === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // COMPLETELY ISOLATED SUCCESS RENDER — CheckoutForm never mounts
  if (paymentStatus === 'success') {
    return <PaymentSuccessScreen />;
  }

  // COMPLETELY ISOLATED FAIL/ERROR RENDER — CheckoutForm never mounts
  if (paymentStatus === 'fail' || paymentStatus === 'error') {
    return <PaymentFailScreen />;
  }

  // No payment_status param — render normal e-commerce checkout
  return <CheckoutForm />;
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECKOUT FORM — all hooks live here, only rendered for normal checkout flow
// ─────────────────────────────────────────────────────────────────────────────
function CheckoutForm() {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart, registerBundleAddedCallback } = useCart() || {};
  const { user, userProfile } = useAuth() || {};
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [stockWarnings, setStockWarnings] = useState([]);
  const [paymentInitLoading, setPaymentInitLoading] = useState(false);
  const [paynkolayFormData, setPaynkolayFormData] = useState(null);
  const [isBankTransferSuccess, setIsBankTransferSuccess] = useState(false);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('credit_card');

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    address: '',
    city: '',
    district: '',
    postalCode: '',
    phone: '',
    email: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Billing state
  const [billingType, setBillingType] = useState('individual');
  const [billingData, setBillingData] = useState({
    taxId: '',
    taxOffice: '',
    companyName: '',
    billingAddress: ''
  });
  const [billingErrors, setBillingErrors] = useState({});

  // On mount: wipe any stale order/draft references from localStorage
  useEffect(() => {
    localStorage.removeItem('order_id');
    localStorage.removeItem('draft_order_id');
    localStorage.removeItem('checkout_order_id');
    localStorage.removeItem('pending_order');
  }, []);

  // Auto-fill form data from userProfile
  useEffect(() => {
    if (userProfile) {
      const savedCity = userProfile?.city || '';
      const savedDistrict = userProfile?.district || '';
      setFormData(prev => ({
        ...prev,
        fullName: userProfile?.full_name || user?.email?.split('@')?.[0] || prev?.fullName,
        phone: userProfile?.phone || user?.phone || prev?.phone,
        address: userProfile?.address_line || prev?.address,
        city: savedCity,
        district: savedDistrict,
        postalCode: userProfile?.zip_code || prev?.postalCode,
        email: user?.email || prev?.email
      }));
      // Auto-fill TC from profile (tc_number field)
      const profileTc = userProfile?.tc_number || userProfile?.tcNumber || '';
      if (profileTc) {
        setBillingData(prev => ({
          ...prev,
          taxId: prev?.taxId || profileTc
        }));
      }
    }
  }, [userProfile, user]);

  const safeCartItems = cartItems || [];
  const safeCartTotal = cartTotal || 0;
  const availableDistricts = formData?.city ? (TURKEY_DISTRICTS?.[formData?.city] || []) : [];

  // ACTION 2: Register a callback in CartContext so that when a bundle is added,
  // any active coupon is automatically cleared.
  const handleBundleAddedClearCoupon = useCallback(() => {
    if (appliedCoupon) {
      setAppliedCoupon(null);
      setCouponDiscount(0);
      setCouponCode('');
      setCouponSuccess('');
      setCouponError('Sepetinize paket eklendiği için aktif kuponunuz kaldırılmıştır.');
    }
  }, [appliedCoupon]);

  useEffect(() => {
    if (typeof registerBundleAddedCallback === 'function') {
      registerBundleAddedCallback(handleBundleAddedClearCoupon);
    }
  }, [registerBundleAddedCallback, handleBundleAddedClearCoupon]);

  // Shipping fee logic
  const FREE_SHIPPING_THRESHOLD = 2500;
  const SHIPPING_COST = 149.90;
  const shippingFee = (safeCartTotal > 0 && safeCartTotal < FREE_SHIPPING_THRESHOLD) ? SHIPPING_COST : 0;

  useEffect(() => {
    checkStockAvailability();
  }, [cartItems]);

  // Guard: if cart is empty and no payment status, redirect to store
  // BUT do NOT redirect if bank transfer just succeeded
  useEffect(() => {
    if (safeCartItems?.length === 0 && !isBankTransferSuccess) {
      const params = new URLSearchParams(window.location.search);
      const status = params?.get('payment_status');
      const resId = params?.get('reservationId');
      if (!status && !resId) {
        navigate('/official-store');
      }
    }
  }, [safeCartItems, isBankTransferSuccess, navigate]);

  const checkStockAvailability = async () => {
    if (!safeCartItems?.length) return;
    try {
      const result = await inventoryService?.checkStockAvailability(safeCartItems);
      if (!result?.available) {
        setError(`Bazı ürünler stokta yok: ${result?.unavailableItems?.map(item => item?.title)?.join(', ')}`);
      } else {
        setError('');
      }
      setStockWarnings(result?.warnings || []);
    } catch (err) {
      console.error('Error checking stock:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    if (name === 'city') {
      setFormData(prev => ({ ...prev, city: value, district: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (formErrors?.[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData?.fullName?.trim()) errors.fullName = 'Ad Soyad gereklidir';
    if (!formData?.email?.trim()) {
      errors.email = 'E-posta adresi gereklidir';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email?.trim())) {
      errors.email = 'Geçerli bir e-posta adresi giriniz';
    }
    if (!formData?.address?.trim()) errors.address = 'Adres gereklidir';
    if (!formData?.city) errors.city = 'Şehir seçiniz';
    if (!formData?.district) errors.district = 'İlçe seçiniz';
    if (!formData?.postalCode?.trim()) {
      errors.postalCode = 'Posta kodu gereklidir';
    } else if (!/^\d{5}$/?.test(formData?.postalCode)) {
      errors.postalCode = 'Geçerli bir posta kodu giriniz (5 haneli)';
    }
    if (!formData?.phone?.trim()) {
      errors.phone = 'Telefon numarası gereklidir';
    } else if (!/^[0-9]{10,11}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      errors.phone = 'Geçerli bir telefon numarası giriniz';
    }

    const bErrors = {};
    if (billingType === 'individual') {
      if (billingData?.taxId?.trim() && !/^\d{11}$/?.test(billingData?.taxId?.trim())) {
        bErrors.taxId = 'TC Kimlik No 11 haneli olmalıdır';
      }
    } else {
      if (!billingData?.companyName?.trim()) bErrors.companyName = 'Şirket adı gereklidir';
      if (!billingData?.taxId?.trim()) {
        bErrors.taxId = 'Vergi numarası gereklidir';
      } else if (!/^\d{10}$/?.test(billingData?.taxId?.trim())) {
        bErrors.taxId = 'Vergi numarası 10 haneli olmalıdır';
      }
      if (!billingData?.taxOffice?.trim()) bErrors.taxOffice = 'Vergi dairesi gereklidir';
      if (!billingData?.billingAddress?.trim()) bErrors.billingAddress = 'Fatura adresi gereklidir';
    }
    setBillingErrors(bErrors);
    setFormErrors(errors);
    return Object.keys(errors)?.length === 0 && Object.keys(bErrors)?.length === 0;
  };

  const applyCoupon = async () => {
    if (!couponCode?.trim()) return;

    // ACTION 2: Block coupon application if cart contains any bundle items
    const hasBundleItem = safeCartItems?.some(item => item?.bundle_instance_id);
    if (hasBundleItem) {
      setCouponError('İndirimli paket/set içeren siparişlerde ekstra indirim kuponu kullanılamaz.');
      return;
    }

    setCouponLoading(true);
    setCouponError('');
    setCouponSuccess('');
    try {
      const result = await couponService?.validateCoupon(couponCode?.trim(), safeCartTotal);
      // validateCoupon throws on failure, so if we reach here it's valid
      setAppliedCoupon(result?.coupon);
      setCouponDiscount(result?.discountAmount || 0);
      setCouponSuccess(`Kupon uygulandı! ${result?.discountAmount?.toFixed(2)} TL indirim kazandınız.`);
    } catch (err) {
      setCouponError(err?.message || 'Geçersiz kupon kodu');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponCode('');
    setCouponSuccess('');
    setCouponError('');
  };

  const finalTotal = Math.max(0, safeCartTotal - couponDiscount);

  // Helper: always extract a string from any error value — defined FIRST so all handlers can use it
  const toErrorString = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err?.message || String(err);
    if (typeof err === 'object') {
      const msg = err?.message || err?.title || err?.error || err?.detail || err?.details;
      if (msg && typeof msg === 'string') return msg;
      if (msg) return String(msg);
      try { return JSON.stringify(err); } catch (_) { return 'Bilinmeyen hata'; }
    }
    return String(err);
  };

  const handleBankTransfer = async () => {
    if (!validateForm()) return;
    setIsProcessing(true);
    setError('');
    try {
      // Apply proportional discount allocation across cart items
      const cartWithDiscounts = couponDiscount > 0
        ? couponService?.calculateProportionalDiscounts(safeCartItems, couponDiscount)
        : safeCartItems;

      const { data, error } = await supabase?.functions?.invoke('process-havale-yeni', {
        body: {
          cart: cartWithDiscounts,
          formData: {
            fullName: formData?.fullName,
            email: user?.email || formData?.email || '',
            phone: formData?.phone,
            address: formData?.address || '',
            city: formData?.city || '',
            district: formData?.district || '',
            postalCode: formData?.postalCode || '',
            tc: billingData?.taxId || '',
          },
          totalAmount: Number(finalTotal + shippingFee) || 0,
          shippingFee: shippingFee,
          discountAmount: couponDiscount || 0,
          couponCode: appliedCoupon?.code || null,
          userId: user?.id || null,
          billingType: billingType === 'corporate' ? 'CORPORATE' : 'INDIVIDUAL',
          companyName: billingType === 'corporate' ? (billingData?.companyName || '') : '',
          taxOffice: billingType === 'corporate' ? (billingData?.taxOffice || '') : '',
          billingAddress: billingType === 'corporate' ? (billingData?.billingAddress || '') : '',
        }
      });

      if (error) throw new Error(error.message || "Bilinmeyen Sunucu Hatası");
      if (data?.success === false) throw new Error(data.error || "Veritabanı Hatası");

      setIsBankTransferSuccess(true);
      clearCart?.();
    } catch (err) {
      setError(toErrorString(err));
    } finally {
      setIsProcessing(false);
    }
    return;
  };

  const handlePaynkolayPayment = async () => {
    if (!validateForm()) return;
    setPaymentInitLoading(true);
    setError('');
    try {
      // Apply proportional discount allocation across cart items
      const cartWithDiscounts = couponDiscount > 0
        ? couponService?.calculateProportionalDiscounts(safeCartItems, couponDiscount)
        : safeCartItems;

      // 1. Send data to secure backend
      const { data, error } = await supabase?.functions?.invoke('process-custom-payment', {
        body: {
          cart: cartWithDiscounts,
          formData: {
            ...formData,
            tc: billingData?.taxId || '',
            email: user?.email || formData?.email || '',
          },
          totalAmount: finalTotal + shippingFee,
          shippingFee: shippingFee,
          discountAmount: couponDiscount || 0,
          couponCode: appliedCoupon?.code || null,
          userId: user?.id,
          clientOrigin: window.location?.origin,
          billingType: billingType === 'corporate' ? 'CORPORATE' : 'INDIVIDUAL',
          companyName: billingType === 'corporate' ? (billingData?.companyName || '') : '',
          taxOffice: billingType === 'corporate' ? (billingData?.taxOffice || '') : '',
          billingAddress: billingType === 'corporate' ? (billingData?.billingAddress || '') : '',
        }
      });

      if (error) throw new Error(error.message);
      if (!data || !data?.success) throw new Error(data?.error || "Sunucu ile ödeme bağlantısı kurulamadı.");

      // 2. Safely redirect to Paynkolay checkout page
      if (data?.paymentUrl) {
        window.location.href = data?.paymentUrl;
      } else if (data?.htmlContent) {
        const newWindow = document.open();
        newWindow?.write(data?.htmlContent);
        newWindow?.close();
      } else {
        throw new Error("Paynkolay'dan geçerli bir ödeme bağlantısı alınamadı.");
      }
    } catch (err) {
      console.error("Paynkolay payment error:", err);
      setError(toErrorString(err));
    } finally {
      setPaymentInitLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    // Sanitize guest checkout text fields to Turkish Title Case before processing
    setFormData(prev => ({
      ...prev,
      fullName: toTurkishTitleCase(prev?.fullName),
      address: toTurkishTitleCase(prev?.address),
    }));
    // --- BANK TRANSFER FLOW: strict fork, must return before Paynkolay ---
    if (paymentMethod === 'bank_transfer' || paymentMethod === 'havale') {
      await handleBankTransfer();
      return; // CRITICAL: prevent fall-through to Paynkolay
    }
    // --- CREDIT CARD FLOW: Paynkolay ---
    if (paymentMethod === 'credit_card' || paymentMethod === 'paynkolay') {
      await handlePaynkolayPayment();
    }
  };

  if (isBankTransferSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-lg p-10 max-w-lg w-full text-center">
            {/* Green checkmark circle */}
            <div className="flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Siparişiniz Başarıyla Alındı!</h1>
            <p className="text-gray-600 mb-2 text-base">
              Havale/EFT bildiriminiz alındı. Ödemeniz onaylandıktan sonra siparişiniz hazırlanacaktır.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              Sipariş detayları e-posta adresinize gönderildi.
            </p>
            {/* Bank info box */}
            <div className="bg-blue-50 p-5 rounded-xl border border-blue-100 mt-6 mb-6 text-left">
              <h3 className="text-sm font-bold text-blue-900 mb-4 flex items-center gap-2">
                🏦 Banka Hesap Bilgilerimiz
              </h3>
              <div className="space-y-2 text-sm text-gray-800">
                <p>
                  <span className="font-semibold text-gray-500">Banka Adı:</span>{' '}
                  <span className="font-bold text-gray-900">İş Bankası</span>
                </p>
                <p>
                  <span className="font-semibold text-gray-500">Hesap Adı:</span>{' '}
                  <span className="font-bold text-gray-900">Serhat ÇOKUÇKUN</span>
                </p>
                <p>
                  <span className="font-semibold text-gray-500">IBAN:</span>{' '}
                  <span className="font-mono font-bold text-gray-950 tracking-wide select-all">
                    TR86 0006 4000 0013 4590 4747 66
                  </span>
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-blue-200">
                <p className="text-xs text-blue-700 font-medium">
                  * Lütfen havale/EFT yaparken açıklama kısmına <span className="font-bold text-blue-900">Sipariş Numaranızı</span> yazmayı unutmayın. Aksi takdirde siparişinizin onaylanması gecikebilir.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/order-history')}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Siparişlerimi Görüntüle
              </button>
              <button
                onClick={() => navigate('/official-store')}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Alışverişe Dön
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!safeCartItems?.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Sepetiniz Boş</h2>
            <p className="text-gray-500 mb-6">Ödeme yapmak için sepetinize ürün ekleyin.</p>
            <button
              onClick={() => navigate('/official-store')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Alışverişe Devam Et
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If Paynkolay form data is ready, render the auto-submit redirect form
  if (paynkolayFormData) {
    return <PaynkolayIframeForm paymentData={paynkolayFormData} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <ShoppingCart className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Ödeme</h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">
              {typeof error === 'string' ? error : (error?.message || error?.title || JSON.stringify(error))}
            </p>
          </div>
        )}

        {stockWarnings?.length > 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            {stockWarnings?.map((warning, idx) => (
              <p key={idx} className="text-yellow-700 text-sm">
                {typeof warning === 'string' ? warning : (warning?.message || warning?.title || JSON.stringify(warning))}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Shipping + Billing + Payment */}
            <div className="lg:col-span-2 space-y-6">

              {/* Guest Nudge Banner */}
              {!user && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 flex gap-3 items-start">
                  <div className="text-blue-500 mt-0.5">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path></svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-blue-900">Misafir Olarak Devam Ediyorsunuz</h4>
                    <p className="text-xs text-blue-800 mt-1 leading-relaxed">
                      Sipariş onayınız ve kargo takip bilgileriniz e-posta adresinize gönderilecektir. Geçmiş siparişlerinizi tek ekrandan kolayca takip edebilmek için ileride sitemize üye olabilirsiniz. <span className="font-semibold">Şu an misafir olarak alışverişi tamamlamanızda hiçbir engel yoktur.</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mx-auto mb-6">
                  <Truck className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Teslimat Adresi</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="Ad Soyad"
                      name="fullName"
                      value={formData?.fullName}
                      onChange={handleInputChange}
                      error={formErrors?.fullName}
                      placeholder="Ad Soyad"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Input
                      label="E-posta Adresi"
                      name="email"
                      type="email"
                      value={formData?.email}
                      onChange={handleInputChange}
                      error={formErrors?.email}
                      placeholder="ornek@email.com"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Input
                      label="Adres"
                      name="address"
                      value={formData?.address}
                      onChange={handleInputChange}
                      error={formErrors?.address}
                      placeholder="Sokak, Mahalle, Bina No, Daire No"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Şehir <span className="text-red-500">*</span></label>
                    <select
                      name="city"
                      value={formData?.city}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors?.city ? 'border-red-400' : 'border-gray-300'}`}
                    >
                      <option value="">Şehir Seçin</option>
                      {TURKEY_CITIES?.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                    {formErrors?.city && <p className="text-red-500 text-xs mt-1">{formErrors?.city}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">İlçe <span className="text-red-500">*</span></label>
                    <select
                      name="district"
                      value={formData?.district}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors?.district ? 'border-red-400' : 'border-gray-300'}`}
                      disabled={!formData?.city}
                    >
                      <option value="">İlçe Seçin</option>
                      {availableDistricts?.map(district => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                    </select>
                    {formErrors?.district && <p className="text-red-500 text-xs mt-1">{formErrors?.district}</p>}
                  </div>
                  <div>
                    <Input
                      label="Posta Kodu"
                      name="postalCode"
                      value={formData?.postalCode}
                      onChange={handleInputChange}
                      error={formErrors?.postalCode}
                      placeholder="34000"
                      required
                    />
                  </div>
                  <div>
                    <Input
                      label="Telefon"
                      name="phone"
                      value={formData?.phone}
                      onChange={handleInputChange}
                      error={formErrors?.phone}
                      placeholder="05XX XXX XX XX"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Billing Info */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mx-auto mb-6">
                  <Building2 className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Fatura Bilgileri</h2>
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="billingType" value="individual" checked={billingType === 'individual'} onChange={() => setBillingType('individual')} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Bireysel</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="billingType" value="corporate" checked={billingType === 'corporate'} onChange={() => setBillingType('corporate')} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Kurumsal</span>
                  </label>
                </div>
                {billingType === 'individual' ? (
                  <Input
                    label="TC Kimlik No (İsteğe Bağlı)"
                    value={billingData?.taxId}
                    onChange={e => setBillingData(prev => ({ ...prev, taxId: e?.target?.value }))}
                    error={billingErrors?.taxId}
                    placeholder="11 haneli TC Kimlik No"
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Şirket Adı"
                        value={billingData?.companyName}
                        onChange={e => setBillingData(prev => ({ ...prev, companyName: e?.target?.value }))}
                        error={billingErrors?.companyName}
                        placeholder="Şirket Adı"
                        required
                      />
                    </div>
                    <Input
                      label="Vergi Numarası"
                      value={billingData?.taxId}
                      onChange={e => setBillingData(prev => ({ ...prev, taxId: e?.target?.value }))}
                      error={billingErrors?.taxId}
                      placeholder="10 haneli Vergi No"
                      required
                    />
                    <Input
                      label="Vergi Dairesi"
                      value={billingData?.taxOffice}
                      onChange={e => setBillingData(prev => ({ ...prev, taxOffice: e?.target?.value }))}
                      error={billingErrors?.taxOffice}
                      placeholder="Vergi Dairesi"
                      required
                    />
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fatura Adresi <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={billingData?.billingAddress}
                        onChange={e => setBillingData(prev => ({ ...prev, billingAddress: e?.target?.value }))}
                        placeholder="Fatura adresi (Sokak, Mahalle, Bina No, Daire No, Şehir)"
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${billingErrors?.billingAddress ? 'border-red-400' : 'border-gray-300'}`}
                        required
                      />
                      {billingErrors?.billingAddress && (
                        <p className="text-red-500 text-xs mt-1">{billingErrors?.billingAddress}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mx-auto mb-6">
                  <CreditCard className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Ödeme Yöntemi</h2>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${paymentMethod === 'credit_card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="paymentMethod" value="credit_card" checked={paymentMethod === 'credit_card'} onChange={() => setPaymentMethod('credit_card')} className="text-blue-600" />
                    <CreditCard className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">Kredi / Banka Kartı</p>
                      <p className="text-xs text-gray-500">Güvenli ödeme altyapısı ile</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${paymentMethod === 'bank_transfer' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="paymentMethod" value="bank_transfer" checked={paymentMethod === 'bank_transfer'} onChange={() => setPaymentMethod('bank_transfer')} className="text-blue-600" />
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">Havale / EFT</p>
                      <p className="text-xs text-gray-500">Banka havalesi ile ödeme</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Right: Order Summary */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Sipariş Özeti</h2>
                <div className="space-y-3 mb-4">
                  {safeCartItems?.map(item => (
                    <div key={item?.id} className="flex items-center gap-3">
                      {item?.image && (
                        <img src={item?.image} alt={item?.imageAlt || item?.title} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item?.title}</p>
                        <p className="text-xs text-gray-500">x{item?.quantity}</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 flex-shrink-0">{(item?.price * item?.quantity)?.toFixed(2)} TL</p>
                    </div>
                  ))}
                </div>

                {/* Coupon */}
                <div className="border-t border-gray-100 pt-4 mb-4">
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={e => setCouponCode(e?.target?.value)}
                      placeholder="Kupon kodu"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!!appliedCoupon}
                    />
                    {appliedCoupon ? (
                      <button type="button" onClick={removeCoupon} className="px-3 py-2 bg-red-100 text-red-600 rounded-lg text-sm hover:bg-red-200 transition-colors">
                        Kaldır
                      </button>
                    ) : (
                      <button type="button" onClick={applyCoupon} disabled={couponLoading || !couponCode?.trim()} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                        {couponLoading ? '...' : 'Uygula'}
                      </button>
                    )}
                  </div>
                  {couponError && <p className="text-red-500 text-xs">{couponError}</p>}
                  {couponSuccess && <p className="text-green-600 text-xs">{couponSuccess}</p>}
                </div>

                {/* Totals */}
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Ara Toplam</span>
                    <span>{safeCartTotal?.toFixed(2)} TL</span>
                  </div>
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>İndirim</span>
                      <span>-{couponDiscount?.toFixed(2)} TL</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Kargo Ücreti</span>
                    <span className={shippingFee === 0 ? 'text-green-600 font-medium' : 'text-gray-900 font-medium'}>
                      {shippingFee === 0 ? 'Ücretsiz' : '₺149,90'}
                    </span>
                  </div>
                  {safeCartTotal > 0 && safeCartTotal < FREE_SHIPPING_THRESHOLD && (
                    <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <p className="text-xs font-semibold text-green-700">
                        🚚 Kargo bedavaya <span className="font-bold">₺{(FREE_SHIPPING_THRESHOLD - safeCartTotal)?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> kaldı!
                      </p>
                    </div>
                  )}
                  {safeCartTotal >= FREE_SHIPPING_THRESHOLD && safeCartTotal > 0 && (
                    <p className="text-xs font-semibold text-green-600">🎉 Tebrikler! Kargonuz Bedava.</p>
                  )}
                  <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-200">
                    <span>Toplam</span>
                    <span>{(finalTotal + shippingFee)?.toFixed(2)} TL</span>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isProcessing || paymentInitLoading}
                  className="w-full mt-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isProcessing || paymentInitLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      {paymentMethod === 'bank_transfer' ? 'Siparişi Onayla' : 'Güvenli Ödeme Yap'}
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-400">
                  <Shield className="w-3 h-3" />
                  <span>256-bit SSL ile güvenli ödeme</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}